import { useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from '@jana-technology/platform-ui'

import { useAppDispatch } from 'app/hooks'
import { useGetBaseSettingsQuery } from 'app/services/api/basesettings'
import Navbar from 'app/layout/Navbar'
import GenericErrorBoundary from 'common/components/ErrorBoundaries'
import PopupNotification from 'common/components/PopupNotification'
import RootRouteReroute from 'common/components/RootRouteReroute'
import { ExportJobProvider } from 'common/contexts/ExportJob'
import { auth0Token } from 'common/utils/authUtils'

import DataManagement from 'features/DataManagement'
import RegulatoryTools from 'features/RegulatoryTools'
import RiskManagement from 'features/RiskManagement'
import SettingsPage from 'features/Settings'
import {
  setBaseSettingsSync,
  setEventAttributesSync
} from 'features/Settings/settingsSlice'

import { useGetDataUpdateEventTablesAttributesQuery } from './services/api/dataUpdate/eventTableAttributes'

const App = () => {
  const { isLoading, isAuthenticated, getTokenSilently, logout } =
    useAuth()

  const { search } = useLocation()
  const dispatch = useAppDispatch()

  const { currentData: baseSettings } = useGetBaseSettingsQuery()

  useEffect(() => {
    if (baseSettings) {
      dispatch(setBaseSettingsSync(baseSettings))
    }
  }, [baseSettings, dispatch])

  const { currentData: eventAttributes } =
    useGetDataUpdateEventTablesAttributesQuery()

  useEffect(() => {
    if (eventAttributes) {
      dispatch(setEventAttributesSync(eventAttributes))
    }
  }, [dispatch, eventAttributes])

  if (isLoading) {
    return <div>Retrieving Authentication Status... Please Wait.</div>
  }

  /*
    TODO: [FE DEBT] Refactor Handling auth0 Token
    - This is a side effect and it should be inside useEffect
    - Putting it in useEffect creates a set of new problems
    - We either need to handle authentication outside of <App /> or update middleware
    - Or we will refactor Auth0ProviderWithHistory and handle it there
   */
  if (isAuthenticated) {
    //* setting this because baseQuery errors out on line 9
    auth0Token.getAccessTokenSilently = getTokenSilently

    auth0Token.setAccessTokenSilentlyFunc(
      getTokenSilently()
        .then((token) => token)
        .catch(logout)
    )
  }

  return (
    <GenericErrorBoundary>
      <ExportJobProvider>
        <div className='flex h-full min-w-320 flex-col'>
          <PopupNotification />
          <Navbar />
          <div className='flex flex-1 overflow-hidden'>
            <Routes>
              <Route path='data-management/*' element={<DataManagement />} />
              <Route path='risk-management/*' element={<RiskManagement />} />
              {!(
                import.meta.env.VITE_DISABLE_REGULATORY_TOOLS?.toLowerCase() ===
                'true'
              ) && (
                <Route
                  path='regulatory-tools/*'
                  element={<RegulatoryTools />}
                />
              )}
              <Route path='settings/*' element={<SettingsPage />} />
              <Route path='/' element={<RootRouteReroute search={search} />} />
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </div>
        </div>
      </ExportJobProvider>
    </GenericErrorBoundary>
  )
}

const NotFoundPage = () => {
  const hidden_version = `h-${import.meta.env.VITE_APP_PACKAGE_VERSION}`

  return <div className={`m-2 ${hidden_version}`}>404 - Not Found</div>
}

export default App
